import {
  createContext,
  useContext,
  useId,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';

/**
 * Wave 5 (BUILD_PLAN PR #17) — minimal controlled-tabs primitive used by
 * /subscription/{id} to split Oversigt + Tasks. Built bare-bones (no
 * external deps) to keep the bundle lean — accessible roving-tab focus
 * ring follows ARIA APG (https://www.w3.org/WAI/ARIA/apg/patterns/tabs).
 *
 * Usage:
 *   <Tabs defaultValue="oversigt">
 *     <TabsList aria-label="Subscription view">
 *       <TabsTrigger value="oversigt">Oversigt</TabsTrigger>
 *       <TabsTrigger value="tasks">Tasks</TabsTrigger>
 *     </TabsList>
 *     <TabsPanel value="oversigt">…</TabsPanel>
 *     <TabsPanel value="tasks">…</TabsPanel>
 *   </Tabs>
 */

interface TabsContextValue {
  value: string;
  setValue: (next: string) => void;
  baseId: string;
}

const TabsCtx = createContext<TabsContextValue | null>(null);

function useTabsCtx(component: string): TabsContextValue {
  const ctx = useContext(TabsCtx);
  if (!ctx) {
    throw new Error(`<${component}> must be rendered inside <Tabs>`);
  }
  return ctx;
}

export interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (next: string) => void;
  children: ReactNode;
  className?: string;
}

export function Tabs({ defaultValue, value, onValueChange, children, className }: TabsProps) {
  const baseId = useId();
  const [internal, setInternal] = useState(defaultValue ?? '');
  const current = value ?? internal;
  const setValue = (next: string) => {
    if (value === undefined) setInternal(next);
    onValueChange?.(next);
  };
  return (
    <TabsCtx.Provider value={{ value: current, setValue, baseId }}>
      <div className={cn('flex flex-col gap-4', className)}>{children}</div>
    </TabsCtx.Provider>
  );
}

export function TabsList({
  children,
  className,
  ariaLabel,
}: {
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
}) {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const triggers = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
    );
    const active = document.activeElement as HTMLElement | null;
    const idx = triggers.findIndex((t) => t === active);
    if (idx < 0) return;
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = triggers[(idx + dir + triggers.length) % triggers.length];
    next?.focus();
    next?.click();
    e.preventDefault();
  };
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      className={cn('inline-flex items-center gap-1 border-b border-line-1 pb-px', className)}
    >
      {children}
    </div>
  );
}

export function TabsTrigger({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  const ctx = useTabsCtx('TabsTrigger');
  const active = ctx.value === value;
  return (
    <button
      type="button"
      role="tab"
      id={`${ctx.baseId}-trigger-${value}`}
      aria-selected={active}
      aria-controls={`${ctx.baseId}-panel-${value}`}
      tabIndex={active ? 0 : -1}
      onClick={() => ctx.setValue(value)}
      className={cn(
        '-mb-px border-b-2 px-3 py-2 text-ui font-medium transition-colors duration-1 ease-brand',
        active ? 'border-ink-0 text-ink-0' : 'border-transparent text-ink-2 hover:text-ink-0',
        'focus-visible:shadow-focus focus-visible:outline-none',
        className,
      )}
    >
      {children}
    </button>
  );
}

export function TabsPanel({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  const ctx = useTabsCtx('TabsPanel');
  if (ctx.value !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${ctx.baseId}-panel-${value}`}
      aria-labelledby={`${ctx.baseId}-trigger-${value}`}
      className={className}
    >
      {children}
    </div>
  );
}
